import { useParams, Link } from 'react-router-dom';
import { mangas } from '@/mocks/mangas';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SEOData from '@/components/feature/SEOData';
import MangaCard from '@/components/base/MangaCard';
import { useScrollToTop } from '@/hooks/useScrollToTop';

const categoryNames: Record<string, string> = {
  rexue: '热血',
  lianai: '恋爱',
  xuanyi: '悬疑',
  qihuan: '奇幻',
  gaoxiao: '搞笑',
  kehuan: '科幻',
  kongbu: '恐怖',
  wuxia: '武侠',
  richang: '日常',
};

export default function Article() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();
  const manga = mangas.find((m) => m.id === Number(id));

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="pt-16 md:pt-18">
          <Breadcrumbs />
        </div>
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
              <i className="ri-emotion-sad-line text-foreground-300 text-4xl"></i>
            </span>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              返回分类浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName = categoryNames[manga.category] || manga.category;
  const relatedMangas = mangas
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  const articleTitle = `${manga.title} - 龙禁漫${categoryName}漫画在线阅读`;
  const articleDescription = `${manga.title}：${manga.description} 作者：${manga.author}，状态：${manga.status}，评分：${manga.rating}分。龙禁漫正版漫画在线阅读平台。`;

  return (
    <>
      <SEOData
        pageType="article"
        title={articleTitle}
        description={articleDescription}
        canonical={`https://longjinman.com/article/${manga.id}`}
        articleData={{
          headline: manga.title,
          datePublished: '2025-01-15',
          dateModified: manga.updateDate,
          author: manga.author,
          image: manga.cover,
        }}
      />
      <Navbar />
      <div className="pt-16 md:pt-18">
        <Breadcrumbs />
      </div>
      <main>
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0 w-full lg:w-[320px]">
                <div className="sticky top-24">
                  <div className="relative rounded-xl overflow-hidden aspect-[3/4] bg-background-100">
                    <img
                      src={manga.cover}
                      alt={manga.title}
                      title={`${manga.title} - 龙禁漫${categoryName}漫画封面`}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        manga.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                      }`}>
                        {manga.status}
                      </span>
                    </div>
                  </div>

                  <div className="mt-5 space-y-3">
                    <Link
                      to="/category"
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-200"
                    >
                      <i className="ri-book-open-line"></i>
                      开始阅读
                    </Link>
                    <button className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200">
                      <i className="ri-bookmark-line"></i>
                      加入书架
                    </button>
                  </div>

                  <div className="mt-5 bg-background-100 rounded-xl p-4 space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">作者</span>
                      <span className="text-foreground-900 font-medium">{manga.author}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">分类</span>
                      <Link to={`/category?type=${manga.category}`} className="text-primary-500 font-medium hover:text-primary-600">
                        {categoryName}
                      </Link>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-500 font-semibold">
                        <i className="ri-star-fill mr-0.5"></i>
                        {manga.rating}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">章节</span>
                      <span className="text-foreground-900 font-medium">{manga.chapters}话</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">阅读量</span>
                      <span className="text-foreground-900 font-medium">
                        {manga.views >= 10000 ? `${(manga.views / 10000).toFixed(1)}万` : manga.views}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-500">更新日期</span>
                      <span className="text-foreground-900 font-medium">{manga.updateDate}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 mb-4">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-800 rounded-full text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  {manga.title}
                </h1>

                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  {manga.description}
                </p>

                <div className="prose prose-sm max-w-none">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-3">作品简介</h2>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {manga.longDescription}
                  </p>
                </div>

                <div className="mt-10 p-5 md:p-6 bg-background-100 rounded-xl">
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">阅读提示</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-500"></i>
                      </span>
                      本作品为龙禁漫正版授权漫画，请放心阅读
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-500"></i>
                      </span>
                      如需离线阅读，点击"开始阅读"后在阅读界面选择下载
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-500"></i>
                      </span>
                      加入书架后可第一时间获取更新通知
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {relatedMangas.length > 0 && (
              <section className="mt-14 md:mt-20 pt-10 border-t border-background-200/70" aria-labelledby="related-title">
                <h2 id="related-title" className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {relatedMangas.map((related) => (
                    <MangaCard
                      key={related.id}
                      id={related.id}
                      title={related.title}
                      author={related.author}
                      cover={related.cover}
                      category={related.category}
                      rating={related.rating}
                      tags={related.tags}
                      status={related.status}
                      views={related.views}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}